/**
 * English formatter for the core 'model' catalogue (W1-03 core-model-ops).
 *
 * Core code cannot import the i18n runtime (src/i18n), but `InvariantViolation.message` and `DocFormatError` need
 * English text. This module formats catalogue messages with the subset of ICU-lite (contract src/i18n/types.ts) that
 * the core catalogues use. The UI renders the same keys and parameters through `i18n.tr` in any locale. Supported:
 *   {name}                                      interpolation (a missing parameter stays as `{name}`)
 *   {name, select, a {...} b {...} other {...}} select on the string value (a missing parameter picks `other`)
 *   {n, plural, =0 {...} one {# x} other {# xs}} English cardinal plural (`offset:N` allowed), `#` = the number
 *   {n, number}                                 the number as written by String()
 *   '' and '{...}'                              apostrophe quoting, as in ICU and src/i18n/icu.ts
 * Parameters are therefore language-neutral (ids, numbers, names, codes); words come from `select` branches.
 */

import catalog from './model.messages';

export type MessageParams = Readonly<Record<string, string | number>>;

type Node =
  | { readonly t: 'text'; readonly v: string }
  | { readonly t: 'arg'; readonly name: string }
  | { readonly t: 'pound' }
  | { readonly t: 'select'; readonly name: string; readonly options: ReadonlyMap<string, readonly Node[]> }
  | {
      readonly t: 'plural';
      readonly name: string;
      readonly offset: number;
      readonly options: ReadonlyMap<string, readonly Node[]>;
    };

/** Malformed message text (a catalogue bug; `formatMessage` callers fall back to the raw text). */
export class MessageSyntaxError extends Error {
  override readonly name = 'MessageSyntaxError';
  readonly pos: number;
  constructor(message: string, pos: number) {
    super(`${message} (at character ${String(pos + 1)})`);
    this.pos = pos;
  }
}

const IDENT_RE = /[A-Za-z0-9_]/;
const SELECTOR_RE = /[A-Za-z0-9_=.-]/;
const MAX_DEPTH = 8;

class Parser {
  private i = 0;
  private readonly s: string;

  constructor(s: string) {
    this.s = s;
  }

  parse(): Node[] {
    const nodes = this.message(false, 0);
    if (this.i < this.s.length) throw new MessageSyntaxError('Unexpected "}"', this.i);
    return nodes;
  }

  private peek(): string {
    return this.s[this.i] ?? '';
  }

  private ws(): void {
    while (this.i < this.s.length && /\s/.test(this.peek())) this.i++;
  }

  private expect(ch: string): void {
    if (this.peek() !== ch) throw new MessageSyntaxError(`Expected "${ch}"`, this.i);
    this.i++;
  }

  private ident(re: RegExp, what: string): string {
    const start = this.i;
    while (this.i < this.s.length && re.test(this.peek())) this.i++;
    if (this.i === start) throw new MessageSyntaxError(`Expected ${what}`, this.i);
    return this.s.slice(start, this.i);
  }

  private message(inPlural: boolean, depth: number): Node[] {
    const nodes: Node[] = [];
    let text = '';
    const flush = (): void => {
      if (text !== '') nodes.push({ t: 'text', v: text });
      text = '';
    };
    while (this.i < this.s.length) {
      const c = this.peek();
      if (c === "'") {
        text += this.quoted(inPlural);
        continue;
      }
      if (c === '}') {
        if (depth === 0) throw new MessageSyntaxError('Unexpected "}"', this.i);
        break;
      }
      if (c === '{') {
        flush();
        nodes.push(this.argument(inPlural, depth + 1));
        continue;
      }
      if (c === '#' && inPlural) {
        flush();
        nodes.push({ t: 'pound' });
        this.i++;
        continue;
      }
      text += c;
      this.i++;
    }
    flush();
    return nodes;
  }

  /** Apostrophe at `this.i`: `''` is a quote; a quote before a syntax character starts a literal run. */
  private quoted(inPlural: boolean): string {
    const next = this.s[this.i + 1];
    if (next === "'") {
      this.i += 2;
      return "'";
    }
    if (next === '{' || next === '}' || next === '|' || (next === '#' && inPlural)) {
      let out = '';
      this.i++;
      while (this.i < this.s.length) {
        const c = this.peek();
        if (c === "'") {
          if (this.s[this.i + 1] === "'") {
            out += "'";
            this.i += 2;
            continue;
          }
          this.i++;
          return out;
        }
        out += c;
        this.i++;
      }
      return out;
    }
    this.i++;
    return "'";
  }

  private argument(inPlural: boolean, depth: number): Node {
    if (depth > MAX_DEPTH) throw new MessageSyntaxError('Message nests too deeply', this.i);
    this.expect('{');
    this.ws();
    const name = this.ident(IDENT_RE, 'an argument name');
    this.ws();
    if (this.peek() === '}') {
      this.i++;
      return { t: 'arg', name };
    }
    this.expect(',');
    this.ws();
    const type = this.ident(IDENT_RE, 'an argument type');
    this.ws();
    switch (type) {
      case 'number':
        // Styles are a display concern of the UI formatter; English core text uses String().
        while (this.i < this.s.length && this.peek() !== '}') this.i++;
        this.expect('}');
        return { t: 'arg', name };
      case 'select': {
        this.expect(',');
        const options = this.options(inPlural, depth);
        return { t: 'select', name, options };
      }
      case 'plural':
      case 'selectordinal': {
        this.expect(',');
        this.ws();
        let offset = 0;
        if (this.s.startsWith('offset:', this.i)) {
          this.i += 'offset:'.length;
          const start = this.i;
          while (/[0-9]/.test(this.peek())) this.i++;
          offset = Number(this.s.slice(start, this.i));
        }
        const options = this.options(true, depth);
        return { t: 'plural', name, offset, options };
      }
      default:
        throw new MessageSyntaxError(`Unknown argument type "${type}"`, this.i);
    }
  }

  /** `key {message} key {message} ... }`; requires an `other` branch. */
  private options(inPlural: boolean, depth: number): Map<string, readonly Node[]> {
    const options = new Map<string, readonly Node[]>();
    for (;;) {
      this.ws();
      if (this.peek() === '}') {
        this.i++;
        break;
      }
      const key = this.ident(SELECTOR_RE, 'a selector');
      this.ws();
      this.expect('{');
      const body = this.message(inPlural, depth);
      this.expect('}');
      options.set(key, body);
    }
    if (!options.has('other')) throw new MessageSyntaxError('Missing "other" branch', this.i);
    return options;
  }
}

const cache = new Map<string, readonly Node[]>();

/** Parses a message (cached by text). Throws `MessageSyntaxError` on malformed text. */
export function parseMessage(text: string): readonly Node[] {
  let nodes = cache.get(text);
  if (nodes === undefined) {
    nodes = new Parser(text).parse();
    cache.set(text, nodes);
  }
  return nodes;
}

/** English cardinal plural category. */
function pluralCategory(n: number): string {
  return n === 1 ? 'one' : 'other';
}

export interface FormatOptions {
  /** Called when a `select` falls back to `other` for a value it has no branch for (catalogue coverage tests). */
  readonly onSelectFallback?: (name: string, value: string) => void;
}

function render(nodes: readonly Node[], params: MessageParams, pound: number | null, opts: FormatOptions): string {
  let out = '';
  for (const n of nodes) {
    switch (n.t) {
      case 'text':
        out += n.v;
        break;
      case 'pound':
        out += pound === null ? '#' : String(pound);
        break;
      case 'arg': {
        const v = params[n.name];
        out += v === undefined ? `{${n.name}}` : String(v);
        break;
      }
      case 'select': {
        const v = params[n.name];
        let body = v === undefined ? undefined : n.options.get(String(v));
        if (body === undefined) {
          if (v !== undefined) opts.onSelectFallback?.(n.name, String(v));
          body = n.options.get('other') ?? [];
        }
        out += render(body, params, pound, opts);
        break;
      }
      case 'plural': {
        const raw = params[n.name];
        const num = typeof raw === 'number' ? raw : Number(raw);
        const exact = Number.isFinite(num) ? n.options.get(`=${String(num)}`) : undefined;
        const value = num - n.offset;
        const body = exact ?? n.options.get(pluralCategory(value)) ?? n.options.get('other') ?? [];
        out += render(body, params, value, opts);
        break;
      }
    }
  }
  return out;
}

/** Formats message text with `params` (see the file header). Malformed text is returned as is. */
export function formatMessage(text: string, params: MessageParams = {}, opts: FormatOptions = {}): string {
  let nodes: readonly Node[];
  try {
    nodes = parseMessage(text);
  } catch (e) {
    if (e instanceof MessageSyntaxError) return text;
    throw e;
  }
  return render(nodes, params, null, opts);
}

/** English text of a 'model' catalogue key (without the namespace), e.g. `inv.keyOrder`; unknown keys verbatim. */
export function formatModelMessage(key: string, params: MessageParams = {}, opts: FormatOptions = {}): string {
  const text = catalog.messages[key];
  return text === undefined ? key : formatMessage(text, params, opts);
}

/** Names of every argument a message reads (tests: every select/arg is fed by its producer). */
export function messageArguments(text: string): ReadonlySet<string> {
  const out = new Set<string>();
  const walk = (nodes: readonly Node[]): void => {
    for (const n of nodes) {
      if (n.t === 'arg') out.add(n.name);
      else if (n.t === 'select' || n.t === 'plural') {
        out.add(n.name);
        for (const body of n.options.values()) walk(body);
      }
    }
  };
  walk(parseMessage(text));
  return out;
}

/** Selector keys of every `select` on argument `name` in a message (tests: every produced code has a branch). */
export function selectKeys(text: string, name: string): ReadonlySet<string> {
  const out = new Set<string>();
  const walk = (nodes: readonly Node[]): void => {
    for (const n of nodes) {
      if (n.t !== 'select' && n.t !== 'plural') continue;
      if (n.t === 'select' && n.name === name) for (const k of n.options.keys()) out.add(k);
      for (const body of n.options.values()) walk(body);
    }
  };
  walk(parseMessage(text));
  return out;
}
